import { IFilterDef, ISimpleFilterModel, SetFilterValuesFuncParams, ValueFormatterParams } from 'ag-grid-community';
import { IBlotterFilteringApi } from 'apiConnector/blotters/common';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { BlotterColumnServerSideFilterOptions, IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import { getFrontLogger } from 'utils/loggers/FrontLogger';
import { getToastUtils } from 'utils/toast';

import { useValueFormatter } from './useStringColumnType';

function toAgGridFilterOptions({
  filterOptions: options,
}: BlotterColumnServerSideFilterOptions):
  | { filterOptions?: ISimpleFilterModel['type'][]; defaultOption?: ISimpleFilterModel['type'] }
  | undefined {
  if ((options?.availableOperations?.length ?? 0) > 0) {
    return { filterOptions: options!.availableOperations, defaultOption: options?.defaultOperation };
  }
}

export const useGetFilterValuesWithApi = (api: IBlotterFilteringApi) => {
  const dispatch = useDispatch();
  return useCallback(
    (params: SetFilterValuesFuncParams): void => {
      const toast = getToastUtils(dispatch);
      const field = params.colDef.field;
      if (field !== undefined) {
        api.fetchFilterValues(field).subscribe({
          next: response => {
            const values = response.values.map(({ value }) => value?.toString() ?? '');
            params.success(values);
          },
          error: err => {
            getFrontLogger().error('Error fetching filter values for field=', field, 'error=', err);
            toast.error(
              'Error when retrieving the filter values, please retry to filter later',
              `Error fetching filter values for field=${field}`,
            );
            params.success([]);
          },
        });
      }
    },
    [dispatch, api],
  );
};

function toYesNo(value: 'true' | 'false'): 'Yes' | 'No' {
  if (value === 'true') {
    return 'Yes';
  }
  if (value === 'false') {
    return 'No';
  }
  return value;
}

function toAgGridFilter(
  filterOptions: BlotterColumnServerSideFilterOptions['filterOptions'],
  getFilterValues: (params: SetFilterValuesFuncParams) => void,
): IFilterDef | undefined {
  switch (filterOptions?.filterType) {
    case 'text':
      return {
        filter: 'agTextColumnFilter',
        filterParams: { ...toAgGridFilterOptions({ filterOptions }), maxNumConditions: 1, debounceMs: 3_000 },
      };
    case 'date':
      return {
        filter: 'agDateColumnFilter',
        filterParams: { ...toAgGridFilterOptions({ filterOptions }), maxNumConditions: 1 },
      };
    case 'number':
      return {
        filter: 'agNumberColumnFilter',
        filterParams: { ...toAgGridFilterOptions({ filterOptions }), maxNumConditions: 1, debounceMs: 3_000 },
      };
    case 'set':
      return {
        filter: 'agSetColumnFilter',
        filterParams: {
          ...toAgGridFilterOptions({ filterOptions }),
          defaultToNothingSelected: true,
          values: getFilterValues,
          refreshValuesOnOpen: true,
        },
      };
    case 'boolean':
      return {
        filter: 'agSetColumnFilter',
        filterParams: {
          ...toAgGridFilterOptions({ filterOptions }),
          suppressSelectAll: true,
          defaultToNothingSelected: true,
          suppressMiniFilter: true,
          values: ['true', 'false'],
          valueFormatter: (params: ValueFormatterParams) => toYesNo(params.value),
        },
      };
    default:
      return { filter: false };
  }
}

export function useTranslateFilterInColumnDefinitions<TData extends object>(
  columns: readonly IBlotterColumnDefinition<TData>[],
  getFilterValues: (params: SetFilterValuesFuncParams) => void,
): IBlotterColumnDefinition<TData>[] {
  const stringValueFormatterCallback = useValueFormatter();

  return useMemo(
    () =>
      columns.map(
        ({
          filterOptions,
          filterParams,
          filterable: _f,
          isPresentInExternalFilter,
          valueFormatter,
          type,
          ...others
        }) => {
          const { filter, filterParams: agGridFilterParams } = isPresentInExternalFilter
            ? {}
            : (toAgGridFilter(filterOptions, getFilterValues) ?? {});

          const mergedFilterParams = { ...agGridFilterParams, ...filterParams };

          let finalValueFormatter = valueFormatter;

          if (!finalValueFormatter && (type === 'string' || filterOptions?.filterType === 'set')) {
            finalValueFormatter = stringValueFormatterCallback;
          }

          if (filterOptions?.filterType === 'set' && finalValueFormatter && !mergedFilterParams.valueFormatter) {
            mergedFilterParams.valueFormatter = (params: ValueFormatterParams) => {
              if (typeof finalValueFormatter === 'function') {
                return finalValueFormatter({ ...params, data: params.data ?? {} });
              }
              return params.value;
            };
          }

          return { ...others, type, valueFormatter: finalValueFormatter, filter, filterParams: mergedFilterParams };
        },
      ),
    [columns, stringValueFormatterCallback, getFilterValues],
  );
}
